import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Calendar, CircleCheck, Clock, Layers, UserRound, Users, X } from 'lucide-react';
import { toast } from 'sonner';
import type { Candidate, CvEvaluationResult } from '../../types';
import { Button } from '../../components/ui/button';
import { WarningModal } from '../../components/AppStatusModal';
import { getApiErrorMessage } from '../../libs/api/errorHandler';
import {
  createCvEvaluation,
  type ApiTalentPoolReason,
  type CvEvaluationPayload,
} from '../../libs/api/pipelineService';
import { MoveToTalentPoolModal } from '../pipeline/TalentPoolModals';
import { CANDIDATES_QUERY_KEY } from './candidateData';
import { CV_EVALUATION_META, CvEvaluationResultBadge, formatEvaluationTime } from './cvEvaluation';

const CONFIRM_COPY: Record<'PASS' | 'FAIL', { title: string; details: string; confirm: string }> = {
  PASS: {
    title: 'Move to Interview',
    details: 'The CV passes screening and moves to the Interview column.',
    confirm: 'Move to Interview',
  },
  FAIL: {
    title: 'Reject candidate',
    details:
      'The CV moves to the Rejected column and a thank-you email is sent to the candidate automatically after 24 hours.',
    confirm: 'Reject',
  },
};

/**
 * Interview / Talent Pool / Rejected buttons of the candidate detail header (CV reviewer view):
 * PASS -> Interview, PENDING -> Talent Pool (reason required), FAIL -> Rejected + thank-you email.
 */
export function CvEvaluationActions({ candidate }: { candidate: Candidate }) {
  const queryClient = useQueryClient();
  const [confirmResult, setConfirmResult] = useState<'PASS' | 'FAIL' | null>(null);
  const [isTalentPoolOpen, setIsTalentPoolOpen] = useState(false);
  // Decided by the backend: assigned to the signed-in reviewer and still in HR Approved.
  const canEvaluate = Boolean(candidate.applicationId) && Boolean(candidate.canEvaluate);

  const evaluationMutation = useMutation({
    mutationFn: (payload: CvEvaluationPayload) =>
      createCvEvaluation(candidate.applicationId!, payload),
    onSuccess: async (_card, payload) => {
      toast.success(`CV moved to ${CV_EVALUATION_META[payload.result].targetStage}`);
      setConfirmResult(null);
      setIsTalentPoolOpen(false);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['candidate-profile'] }),
        queryClient.invalidateQueries({ queryKey: CANDIDATES_QUERY_KEY }),
        queryClient.invalidateQueries({ queryKey: ['pipeline-board'] }),
      ]);
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error, 'Failed to save the CV evaluation'));
    },
  });

  const submit = (result: CvEvaluationResult, talentPoolReason?: ApiTalentPoolReason, note = '') =>
    evaluationMutation.mutate({
      result,
      ...(result === 'PENDING'
        ? { talent_pool_reason: talentPoolReason, talent_pool_note: note || undefined }
        : {}),
    });

  const isDisabled = !canEvaluate || evaluationMutation.isPending;
  const disabledHint = canEvaluate
    ? undefined
    : 'Available when HR assigns this CV to you in HR Approved (one evaluation per CV)';
  const confirmCopy = confirmResult ? CONFIRM_COPY[confirmResult] : null;

  return (
    <>
      <Button
        type="button"
        size="sm"
        className="text-sm"
        disabled={isDisabled}
        title={disabledHint}
        onClick={() => setConfirmResult('PASS')}
      >
        <Calendar size={14} /> Interview
      </Button>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="text-sm"
        disabled={isDisabled}
        title={disabledHint}
        onClick={() => setIsTalentPoolOpen(true)}
      >
        <Layers size={14} /> Talent Pool
      </Button>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="border-error/40 bg-error-muted text-sm text-error-muted-foreground hover:bg-error-muted/70 hover:text-error-muted-foreground"
        disabled={isDisabled}
        title={disabledHint}
        onClick={() => setConfirmResult('FAIL')}
      >
        <X size={14} /> Rejected
      </Button>

      <WarningModal
        open={confirmCopy !== null}
        onOpenChange={(open) => {
          if (!open && !evaluationMutation.isPending) setConfirmResult(null);
        }}
        title={confirmCopy?.title ?? ''}
        description={`${candidate.name} · ${candidate.role}`}
        details={confirmCopy?.details}
        confirmText={evaluationMutation.isPending ? 'Saving...' : confirmCopy?.confirm}
        cancelText="Cancel"
        onConfirm={() => {
          if (confirmResult) submit(confirmResult);
        }}
      />

      <MoveToTalentPoolModal
        card={
          isTalentPoolOpen
            ? {
                name: candidate.name,
                role: candidate.role,
                stage: 'SCREENING',
              }
            : null
        }
        submitting={evaluationMutation.isPending}
        onCancel={() => setIsTalentPoolOpen(false)}
        onConfirm={(reason, note) => submit('PENDING', reason, note)}
      />
    </>
  );
}

/**
 * "CV Evaluation" rows under Interview Assessment: who HR assigned, then who evaluated the CV,
 * with which result and when (one evaluation per CV).
 */
export function CvEvaluationSummary({ candidate }: { candidate: Candidate }) {
  // Newest first (API order).
  const latest = candidate.cvEvaluations?.[0];
  const assignedNames = (candidate.assignedReviewers ?? [])
    .map((reviewer) => reviewer.name ?? reviewer.email)
    .join(', ');

  return (
    <div className="mt-4 border-t border-border-subtle pt-4">
      <div className="mb-3 text-[10px] font-bold uppercase text-text-subtle">CV Evaluation</div>
      <div className="space-y-2 text-sm text-text-main">
        <div className="flex min-w-0 items-center gap-2">
          <Users size={14} className="shrink-0 text-text-subtle" />
          <span className="shrink-0 font-medium text-text-subtle">Assigned to:</span>
          <span className="truncate font-semibold" title={assignedNames}>
            {assignedNames || '-'}
          </span>
        </div>
        <div className="flex min-w-0 items-center gap-2">
          <UserRound size={14} className="shrink-0 text-text-subtle" />
          <span className="shrink-0 font-medium text-text-subtle">Evaluated by:</span>
          <span className="truncate font-semibold">
            {latest ? (latest.evaluatedByName ?? 'Unknown') : '-'}
          </span>
        </div>
        <div className="flex min-w-0 items-center gap-2">
          <CircleCheck size={14} className="shrink-0 text-text-subtle" />
          <span className="shrink-0 font-medium text-text-subtle">Result:</span>
          {latest ? <CvEvaluationResultBadge result={latest.result} /> : <span>-</span>}
        </div>
        <div className="flex min-w-0 items-center gap-2">
          <Clock size={14} className="shrink-0 text-text-subtle" />
          <span className="shrink-0 font-medium text-text-subtle">Evaluated at:</span>
          <span className="font-semibold">
            {latest ? formatEvaluationTime(latest.evaluatedAt) : '-'}
          </span>
        </div>
      </div>
    </div>
  );
}
